import type { PhotobookProject } from './schemas.js';

export type HistoryEntry = {
  label: string;
  undo: () => PhotobookProject;
  redo: () => PhotobookProject;
};

/**
 * Command-based undo/redo. Stores inverse patches as functions rather than
 * full project clones per pointer move. Call begin()/commit() around gestures.
 */
export class ProjectHistory {
  private undoStack: HistoryEntry[] = [];
  private redoStack: HistoryEntry[] = [];
  private draft: {
    label: string;
    before: PhotobookProject;
  } | null = null;
  readonly maxEntries: number;

  constructor(maxEntries = 100) {
    this.maxEntries = maxEntries;
  }

  get canUndo(): boolean {
    return this.undoStack.length > 0;
  }

  get canRedo(): boolean {
    return this.redoStack.length > 0;
  }

  begin(label: string, before: PhotobookProject): void {
    this.draft = { label, before: structuredClone(before) };
  }

  commit(after: PhotobookProject): void {
    if (!this.draft) return;
    const before = this.draft.before;
    const label = this.draft.label;
    const afterClone = structuredClone(after);
    this.undoStack.push({
      label,
      undo: () => structuredClone(before),
      redo: () => structuredClone(afterClone),
    });
    if (this.undoStack.length > this.maxEntries) {
      this.undoStack.shift();
    }
    this.redoStack = [];
    this.draft = null;
  }

  cancel(): void {
    this.draft = null;
  }

  push(label: string, before: PhotobookProject, after: PhotobookProject): void {
    this.begin(label, before);
    this.commit(after);
  }

  undo(_current: PhotobookProject): PhotobookProject | null {
    const entry = this.undoStack.pop();
    if (!entry) return null;
    const restored = entry.undo();
    this.redoStack.push(entry);
    return restored;
  }

  redo(_current: PhotobookProject): PhotobookProject | null {
    const entry = this.redoStack.pop();
    if (!entry) return null;
    const restored = entry.redo();
    this.undoStack.push(entry);
    return restored;
  }

  clear(): void {
    this.undoStack = [];
    this.redoStack = [];
    this.draft = null;
  }
}
